import React from 'react'
import PropTypes from 'prop-types'
import {useHistory} from 'react-router-dom'

// Chakra Components
import {
    Box,
    Text,
    Button,
    Flex,
    Stack,
    Image,
    Heading,
    Divider,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useProducts} from '@salesforce/commerce-sdk-react'
import {useShopperBasketsMutation} from '@salesforce/commerce-sdk-react'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {FormattedMessage} from 'react-intl'
import useTagManager from '../../hooks/use-tag-manager'
const GiftCertificateItems = ({
    shipmentData,
    products: pwaProducts,
    giftCertificateItems = [],
    basket
}) => {
    const history = useHistory()
    const tagManager = useTagManager()
    const mutation = useShopperBasketsMutation('removeGiftCertificateItemFromBasket')
    const cartStyles = useMultiStyleConfig('CartStyles')

    const {
        app: {commerceAPI}
    } = getConfig()
    const {eGiftCards} = commerceAPI.storefront || {}

    // Default config fallback
    const defaultConfigs = {
        amounts: [25, 50, 75, 100, 150, 200],
        products: {}
    }

    const parseConfigs = () => {
        if (!eGiftCards) return defaultConfigs
        try {
            const parsed = JSON.parse(eGiftCards)
            return parsed.amounts && parsed.products ? parsed : defaultConfigs
        } catch (err) {
            console.warn('Error parsing eGiftCards JSON:', err.message)
            return defaultConfigs
        }
    }

    const giftCertConfigs = parseConfigs()

    const productIds = giftCertificateItems?.map(({c_productID}) => c_productID).join(',') ?? ''

    // Fetch product data when selectedId changes
    const {data} = useProducts({
        parameters: {
            ids: productIds
        }
    })

    const products = data?.data || []

    const giftCertificates = giftCertificateItems.map((item) => {
        const product = products.find((p) => p.id === item.c_productID)

        const image = giftCertConfigs.products[item.c_productID]

        return {
            ...product,
            ...item,
            image
        }
    })

    const removeGiftCert = async (giftCertificateItemId) => {
        const basketId = basket.basketId

        mutation.mutate({
            parameters: {
                basketId: basketId,
                giftCertificateItemId: giftCertificateItemId
            }
        })
    }
    const categorizedProducts = shipmentData?.[0]?.categorizedProducts
    return (
        <>
            {giftCertificates.map((giftCertificateItem, index) => {
                return (
                    <React.Fragment key={index}>
                        {(!!categorizedProducts?.regularProducts?.length ||
                            !!categorizedProducts?.bonusProducts?.length) &&
                            (index !== giftCertificates?.length - 1 ||
                                giftCertificates?.length === 1) && (
                                <Divider
                                    color="gray.700"
                                    margin="30px 0px 30px 0px"
                                    borderColor="gray.700"
                                    className="divider-line"
                                    orientation="horizontal"
                                />
                            )}
                        <Box
                            key={index}
                            data-uuid={giftCertificateItem.giftCertificateItemId}
                            position="relative"
                            className="sf-cart-item"
                            data-testid={`sf-cart-item-${
                                giftCertificateItem.c_productID
                                    ? giftCertificateItem.c_productID
                                    : ''
                            }`}
                        >
                            <Stack
                                layerStyle="cardBordered"
                                align="flex-start"
                                className="cart-item-stack-wrapper"
                            >
                                <Flex
                                    alignItems="flex-start"
                                    backgroundColor="white"
                                    className="cart-item-flex"
                                    {...cartStyles.lineItems.item.itemFlex}
                                >
                                    <Box
                                        className="item-image-wrapper"
                                        display="flex"
                                        flexDirection="column"
                                        alignItems="center"
                                    >
                                        <Box
                                            className="image-box-wrapper"
                                            {...cartStyles.lineItems.item.imageBox.base}
                                        >
                                            <Box
                                                className="image-box"
                                                {...cartStyles.lineItems.item.imageWrapper}
                                            >
                                                {giftCertificateItem.image && (
                                                    <Image
                                                        alt="Gift Card Image"
                                                        className="product-image"
                                                        src={giftCertificateItem.image}
                                                        ignoreFallback={true}
                                                        {...cartStyles.lineItems.item.imageBox
                                                            .image}
                                                    />
                                                )}
                                            </Box>
                                        </Box>
                                    </Box>
                                    <Stack
                                        className="item-stack"
                                        {...cartStyles.lineItems.item.itemStack}
                                    >
                                        <Stack
                                            spacing={1}
                                            className="name-price-stack"
                                            {...cartStyles.lineItems.item.namePriceStack.base}
                                        >
                                            <Box
                                                display="grid"
                                                gap="20px"
                                                className="name-variation-badge"
                                            >
                                                <Box
                                                    className="name-variation-box"
                                                    {...cartStyles.lineItems.item.nameVariation}
                                                >
                                                    <Box
                                                        className="name-box"
                                                        display="flext"
                                                        justifyContent="space-between"
                                                        {...cartStyles.lineItems.item.nameWrapper
                                                            .base}
                                                    >
                                                        <Heading
                                                            as="h2"
                                                            fontSize="md"
                                                            display="flext"
                                                            className="product-name-heading"
                                                            {...cartStyles.lineItems.item.name}
                                                        >
                                                            <FormattedMessage
                                                                id="gift.certificate"
                                                                defaultMessage="Gift Certificate"
                                                            />
                                                        </Heading>
                                                    </Box>
                                                    <Box
                                                        className="mobile-price-wrapper"
                                                        display={{base: 'block', xl: 'none'}}
                                                    >
                                                        <Text
                                                            as="span"
                                                            display="flex"
                                                            {...cartStyles.lineItems.item.giftCert
                                                                .price}
                                                        >
                                                            {
                                                                giftCertificateItem.c_selectedCurrencySymbol
                                                            }
                                                            {giftCertificateItem.amount.toFixed(2)}
                                                        </Text>
                                                    </Box>
                                                    <Box className="recipient-wrapper">
                                                        <Text
                                                            as="span"
                                                            display="inline-block"
                                                            {...cartStyles.lineItems.item.giftCert
                                                                .deliveryLabel}
                                                        >
                                                            <FormattedMessage
                                                                id="cart_giftcert.delivered.by.email"
                                                                defaultMessage="Gift certificate will be delivered by email."
                                                            />
                                                        </Text>
                                                    </Box>
                                                    <Box>
                                                        <Box className="recipient-wrapper">
                                                            <Text
                                                                as="span"
                                                                display="inline-block"
                                                                {...cartStyles.lineItems.item
                                                                    .giftCert.toFrom}
                                                            >
                                                                <FormattedMessage
                                                                    id="cart_recipient.to"
                                                                    defaultMessage="To:"
                                                                />
                                                            </Text>
                                                            <Text
                                                                as="span"
                                                                display="inline-block"
                                                                {...cartStyles.lineItems.item
                                                                    .giftCert.recipientSender}
                                                            >
                                                                {giftCertificateItem.recipientName},{' '}
                                                            </Text>
                                                            <Text
                                                                as="span"
                                                                {...cartStyles.lineItems.item
                                                                    .giftCert.recipientEmail}
                                                            >
                                                                {giftCertificateItem.recipientEmail}
                                                            </Text>
                                                        </Box>
                                                        <Box className="sender-wrapper">
                                                            <Text
                                                                as="span"
                                                                display="inline-block"
                                                                {...cartStyles.lineItems.item
                                                                    .giftCert.toFrom}
                                                            >
                                                                <FormattedMessage
                                                                    id="cart_sender.from"
                                                                    defaultMessage="From:"
                                                                />
                                                            </Text>
                                                            <Text
                                                                as="span"
                                                                display="inline-block"
                                                                {...cartStyles.lineItems.item
                                                                    .giftCert.recipientSender}
                                                            >
                                                                {giftCertificateItem.senderName}
                                                            </Text>
                                                        </Box>
                                                    </Box>
                                                    <Box display="flex" gap="20px" marginTop="10px">
                                                        <Button
                                                            variant="link"
                                                            onClick={() => {
                                                                const lineItemId =
                                                                    giftCertificateItem.giftCertificateItemId
                                                                history.push(
                                                                    `/giftcertedit?GiftCertificateLineItemID=${lineItemId}`
                                                                )
                                                            }}
                                                            {...cartStyles.lineItems.item
                                                                .secondaryButtons}
                                                        >
                                                            <FormattedMessage
                                                                id="cart_giftCert.edit.button"
                                                                defaultMessage="Edit"
                                                            />
                                                        </Button>
                                                        <Button
                                                            variant="link"
                                                            onClick={() => {
                                                                const cartDivs =
                                                                    document.querySelectorAll(
                                                                        '.sf-cart-item'
                                                                    )
                                                                const uuid =
                                                                    giftCertificateItem.giftCertificateItemId // NOTE: We are getting index like this because of hidden CB metals and stones.
                                                                const prodIndex = [
                                                                    ...cartDivs
                                                                ].findIndex(
                                                                    (el) => el.dataset.uuid === uuid
                                                                )
                                                                window.updatedIndex = prodIndex + 1
                                                                removeGiftCert(
                                                                    giftCertificateItem.giftCertificateItemId
                                                                )

                                                                tagManager.removeFromCart(
                                                                    giftCertificateItem,
                                                                    pwaProducts[
                                                                        giftCertificateItem?.id
                                                                    ],
                                                                    1,
                                                                    basket?.couponItems
                                                                        ?.map((c) => c.code)
                                                                        .join(',') || ''
                                                                )
                                                            }}
                                                            {...cartStyles.lineItems.item
                                                                .secondaryButtons}
                                                        >
                                                            <FormattedMessage
                                                                id="cart_giftCert.remove.button"
                                                                defaultMessage="Remove"
                                                            />
                                                        </Button>
                                                    </Box>
                                                </Box>
                                            </Box>
                                            <Box className="desktop-price-wrapper">
                                                <Text
                                                    as="span"
                                                    display={{base: 'none', xl: 'block'}}
                                                    {...cartStyles.lineItems.item.giftCert.price}
                                                >
                                                    {giftCertificateItem.c_selectedCurrencySymbol}
                                                    {giftCertificateItem.amount.toFixed(2)}
                                                </Text>
                                            </Box>
                                        </Stack>
                                    </Stack>
                                </Flex>
                            </Stack>
                        </Box>
                        {index !== giftCertificates?.length - 1 && (
                            <Divider
                                color="gray.700"
                                margin="30px 0px 30px 0px"
                                borderColor="gray.700"
                                className="divider-line"
                                orientation="horizontal"
                            />
                        )}
                    </React.Fragment>
                )
            })}
        </>
    )
}

GiftCertificateItems.propTypes = {
    giftCertificateItems: PropTypes.arrayOf(PropTypes.object),
    shipmentData: PropTypes.any,
    products: PropTypes.any,
    basket: PropTypes.object
}

export default GiftCertificateItems
